import pick from 'lodash/pick';

import {SharableEntityType} from 'common/report/util/saved-entities/types';

export const eventPrefix = `Sharing`;

let session = null;

function getTime() {
  return Date.now() / 1000;
}

export interface SharingTrackArgs {
  eventName: SharingTrackEvents;
  props: {[key in SharingTrackProperties]?: any};
}

/** Locations where a user can copy share link */
export const enum CopyLinkUISource {
  ShareModal = `Share Modal`,
  CreateEntityModal = `Create Entity Modal (Save before share)`,
  SplitButton = `Split Button`,
}

/** Locations where a user can open a share modal by clicking a button */
export const enum ShareButtonUISource {
  ContextPanel = `Context Panel`,
  ShareDropdownManageSharing = `Manage Sharing (Share Dropdown Item)`,
  OldSharingButton = `Old Sharing Button`,
  SplitShareButton = `Split Sharing Button`,
}

export const enum ShareModalType {
  Upsell = `Upsell`,
  Regular = `Regular`,
}

export const enum SharingTrackEvents {
  /** Share Dropdown Menu opens a menu with Copy Link and Manage Sharing items */
  ShareDropdownMenuClick = `Share Dropdown Menu Click`,
  /** Opens a share modal */
  ShareButtonClick = `Share Button Click`,
  /** Sharing modal opened */
  ModalOpen = `Modal Opened`,
  /** Sharing modal closed */
  ModalClosed = `Modal Closed`,
  /** New grants given */
  AddNewGrants = `Add Grants`,
  /** Existing grant deleted */
  DeleteGrants = `Delete Grants`,
  /** Existing grant modified between view and edit */
  UpdateGrants = `Update Grants`,
  /** Short-link Generated */
  ShortLink = `Short Link`,
  /** Save and Share clicked */
  SaveAndShare = `Save and Share`,
  /** Help Docs Click */
  HelpClick = `Help Click`,
  /** Copying a short link failed */
  CopyLinkFailed = `Copying Short Link Failed`,
  /** Global Access */
  GlobalAccessSet = `Global Access Set on Save`,
  /** Public Dashboard */
  PublicDashboardUpdated = `Public Dashboard Updated`,
  /** Public Dashboard Password */
  PublicDashboardPasswordUpdated = `Public Dashboard Password Updated`,
}

export const enum SharingTrackProperties {
  /** Type of entity being changed (bookmarks, cohorts, dashboards) */
  EntityType = `Entity Type`,
  /** Entity Id */
  EntityId = `Entity Id`,
  /** View or edit permission (true, false) */
  CanEdit = `Can Edit`,
  /** The entity has existing grants (true, false) */
  HasGrants = `Existing Grants`,
  /** Number of users added during a `Add Grants` */
  UserCount = `Users Count`,
  /** Number of teams added during a `Add Grants` */
  TeamCount = `Teams Count`,
  /** Number of actions taken since the modal was opened */
  ActionsCount = `Actions Count`,
  /** Time the modal was opened unix timestamp */
  OpenedAt = `Opened At`,
  /** Time the modal was closed unix timestamp */
  ClosedAt = `Closed At`,
  /** Project grant impacted (true) */
  ProjectGrant = `Project Grant`,
  /** Backend generated code for url sharing */
  ShortCode = `short_code`,
  /** An entity is selected for Share */
  IsEntitySelected = `Is Entity Selected`,
  /** Location where a user copied link */
  CopyLinkUISource = `Copy Link Locale`,
  /** Type of Share modal (Upsell or Regular) */
  ShareModalType = `Share Modal Type`,
  /** A user clicked the share upsell button*/
  ClickedUpsell = `Clicked Upsell Button`,
  /** Location where a share button opened a share modal */
  ShareButtonUISource = `Share button Locale`,
  /** Global Access Type */
  GlobalAccessType = `Global accees type`,
  /** Public Dashboard */
  PublicDashboardEnabled = `Public Dashboard Enabled`,
}

function createSession(type: SharableEntityType, id) {
  clearSession();
  if (session && getTime() - session[SharingTrackProperties.OpenedAt] < 1) {
    // an existing session was just created (probably from the share button), don't reset it
    return;
  }
  session = {
    [SharingTrackProperties.EntityType]: type,
    [SharingTrackProperties.EntityId]: id,
    [SharingTrackProperties.OpenedAt]: getTime(),
    [SharingTrackProperties.ActionsCount]: 0,
    [SharingTrackProperties.ClickedUpsell]: false,
  };
}

function actionInc() {
  session[SharingTrackProperties.ActionsCount]++;
}

export function commonSharingTrackProps() {
  return pick(session, [SharingTrackProperties.EntityType, SharingTrackProperties.EntityId]);
}

export function clearSession() {
  session = null;
}

export function getSession() {
  return session;
}

export const track = {
  helpClick(): SharingTrackArgs {
    return {eventName: SharingTrackEvents.HelpClick, props: {}};
  },

  shortLink(shortCode: string, uiSource: CopyLinkUISource): SharingTrackArgs {
    if (getSession()) {
      actionInc();
    }
    return {
      eventName: SharingTrackEvents.ShortLink,
      props: {
        [SharingTrackProperties.ShortCode]: shortCode,
        [SharingTrackProperties.CopyLinkUISource]: uiSource,
      },
    };
  },

  /** Use CurrentUrl to determine which Urls caused the failure */
  shortLinkCopyFailed(source: CopyLinkUISource): SharingTrackArgs {
    return {
      eventName: SharingTrackEvents.CopyLinkFailed,
      props: {
        [SharingTrackProperties.CopyLinkUISource]: source,
      },
    };
  },

  shareDropdownMenuClick(type: SharableEntityType, isEntitySelected: boolean) {
    return {
      eventName: SharingTrackEvents.ShareDropdownMenuClick,
      props: {
        [SharingTrackProperties.EntityType]: type,
        [SharingTrackProperties.IsEntitySelected]: isEntitySelected,
      },
    };
  },

  shareButtonClick(source: ShareButtonUISource): SharingTrackArgs {
    return {
      eventName: SharingTrackEvents.ShareButtonClick,
      props: {
        [SharingTrackProperties.ShareButtonUISource]: source,
      },
    };
  },

  opened(type: SharableEntityType, id): SharingTrackArgs {
    createSession(type, id);
    return {eventName: SharingTrackEvents.ModalOpen, props: {}};
  },

  sharingUpsellBannerClick() {
    session[SharingTrackProperties.ClickedUpsell] = true;
  },

  newGrant(canEdit: boolean, {usersCount, teamsCount}: {usersCount: number; teamsCount: number}): SharingTrackArgs {
    actionInc();
    return {
      eventName: SharingTrackEvents.AddNewGrants,
      props: {
        [SharingTrackProperties.CanEdit]: canEdit,
        [SharingTrackProperties.UserCount]: usersCount,
        [SharingTrackProperties.TeamCount]: teamsCount,
      },
    };
  },

  newProjectGrant(): SharingTrackArgs {
    actionInc();
    return {
      eventName: SharingTrackEvents.AddNewGrants,
      props: {
        [SharingTrackProperties.ProjectGrant]: true,
        [SharingTrackProperties.CanEdit]: false,
      },
    };
  },

  removeProjectGrant(canEdit: boolean): SharingTrackArgs {
    actionInc();
    return {
      eventName: SharingTrackEvents.DeleteGrants,
      props: {
        [SharingTrackProperties.ProjectGrant]: true,
        [SharingTrackProperties.CanEdit]: canEdit,
      },
    };
  },

  updateProjectGrant(canEdit: boolean): SharingTrackArgs {
    actionInc();
    return {
      eventName: SharingTrackEvents.UpdateGrants,
      props: {
        [SharingTrackProperties.ProjectGrant]: true,
        [SharingTrackProperties.CanEdit]: canEdit,
      },
    };
  },

  updateGrant(canEdit: boolean, {usersCount, teamsCount}: {usersCount: number; teamsCount: number}): SharingTrackArgs {
    actionInc();
    return {
      eventName: SharingTrackEvents.UpdateGrants,
      props: {
        [SharingTrackProperties.CanEdit]: canEdit,
        [SharingTrackProperties.TeamCount]: teamsCount,
        [SharingTrackProperties.UserCount]: usersCount,
      },
    };
  },

  removeGrant(canEdit: boolean, {usersCount, teamsCount}: {usersCount: number; teamsCount: number}): SharingTrackArgs {
    actionInc();
    return {
      eventName: SharingTrackEvents.DeleteGrants,
      props: {
        [SharingTrackProperties.CanEdit]: canEdit,
        [SharingTrackProperties.UserCount]: usersCount,
        [SharingTrackProperties.TeamCount]: teamsCount,
      },
    };
  },

  /** We track the share modal type on closed because on opened event, we can't guarantee that the APIs used to determine
   * share modal type are finished.
   */
  closed(modalType: ShareModalType): SharingTrackArgs {
    return {
      eventName: SharingTrackEvents.ModalClosed,
      props: {
        [SharingTrackProperties.ClosedAt]: getTime(),
        [SharingTrackProperties.ShareModalType]: modalType,
        ...pick(session, [
          SharingTrackProperties.OpenedAt,
          SharingTrackProperties.ActionsCount,
          SharingTrackProperties.ClickedUpsell,
        ]),
      },
    };
  },

  publicDashboardUpdated(enabled: boolean): SharingTrackArgs {
    return {
      eventName: SharingTrackEvents.PublicDashboardUpdated,
      props: {
        [SharingTrackProperties.PublicDashboardEnabled]: enabled,
      },
    };
  },
};
